import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Gavel, RotateCcw, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MarketCard } from "@/components/MarketCard";
import { SampleBanner } from "@/components/SampleBanner";
import { fetchMarket } from "@/lib/api";

const NONE = "__none__";
const VIEW_OPTIONS = [
    { value: "5000", label: "5K+ views" },
    { value: "10000", label: "10K+ views" },
    { value: "25000", label: "25K+ views" },
    { value: "50000", label: "50K+ views" },
    { value: "100000", label: "100K+ views" },
];

/** Live auction grid: filter by niche, slot type, min views and event date. */
export default function Market() {
    const [params, setParams] = useSearchParams();

    const niche = params.get("niche") || "";
    const slot = params.get("slot") || "";
    const platform = params.get("platform") || "";
    const views = params.get("views") || "";
    const date = params.get("date") || "";
    const all = params.get("all") === "1";

    const query = useMemo(() => {
        const q = {};
        if (niche) q.niche = niche;
        if (slot) q.slot_type = slot;
        if (platform) q.platform = platform;
        if (views) q.min_views = views;
        if (date) q.event_from = date;
        if (all) q.status = "all";
        return q;
    }, [niche, slot, platform, views, date, all]);

    const market = useQuery({
        queryKey: ["market", query],
        queryFn: () => fetchMarket(query),
        refetchInterval: 15000,
    });

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        setParams(next, { replace: true });
    };

    const clearAll = () => setParams({}, { replace: true });
    const hasFilters = !!(niche || slot || platform || views || date || all);
    const listings = market.data?.listings || [];
    const niches = market.data?.niches || [];
    const slotTypes = market.data?.slot_types || [];
    const platforms = market.data?.platforms || [];

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight inline-flex items-center gap-3">
                        <Gavel className="h-7 w-7 text-brand" /> Sponsor auctions
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                        Concrete slots from fitness creators. Every card: niche, views, slot, current bid, time left.
                    </p>
                </div>
                <Button
                    asChild
                    data-testid="market-new-listing"
                    className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold shrink-0"
                >
                    <Link to="/market/new">List a slot</Link>
                </Button>
            </div>
            <SampleBanner className="mb-6" />

            <Card className="bg-card border-border p-4 mb-6" data-testid="market-filters">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-3">
                    <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                    {hasFilters ? (
                        <button
                            data-testid="market-clear-filters"
                            onClick={clearAll}
                            className="ml-auto inline-flex items-center gap-1 text-brand hover:underline normal-case tracking-normal"
                        >
                            <RotateCcw className="h-3 w-3" /> Clear all
                        </button>
                    ) : null}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                    <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Niche</label>
                        <Select value={niche || NONE} onValueChange={(v) => setParam("niche", v === NONE ? "" : v)}>
                            <SelectTrigger data-testid="filter-niche"><SelectValue placeholder="All niches" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>All niches</SelectItem>
                                {niches.map((n) => (
                                    <SelectItem key={n} value={n}>{n}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Slot type</label>
                        <Select value={slot || NONE} onValueChange={(v) => setParam("slot", v === NONE ? "" : v)}>
                            <SelectTrigger data-testid="filter-slot"><SelectValue placeholder="All slots" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>All slots</SelectItem>
                                {slotTypes.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Platform</label>
                        <Select value={platform || NONE} onValueChange={(v) => setParam("platform", v === NONE ? "" : v)}>
                            <SelectTrigger data-testid="filter-platform"><SelectValue placeholder="All platforms" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>All platforms</SelectItem>
                                {platforms.map((p) => (
                                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Min avg views</label>
                        <Select value={views || NONE} onValueChange={(v) => setParam("views", v === NONE ? "" : v)}>
                            <SelectTrigger data-testid="filter-views"><SelectValue placeholder="Any views" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>Any views</SelectItem>
                                {VIEW_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Event on or after</label>
                        <Input
                            type="date"
                            data-testid="filter-date"
                            value={date}
                            onChange={(e) => setParam("date", e.target.value)}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Show ended</label>
                        <Button
                            variant={all ? "default" : "outline"}
                            data-testid="filter-ended"
                            onClick={() => setParam("all", all ? "" : "1")}
                            className="w-full font-semibold"
                        >
                            {all ? "Including ended" : "Live only"}
                        </Button>
                    </div>
                </div>
            </Card>

            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted-foreground tabular" data-testid="market-count">
                    {market.isLoading ? "Loading…" : `${listings.length} auction${listings.length === 1 ? "" : "s"}`}
                </p>
            </div>

            {market.isLoading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} className="h-72 rounded-2xl" />
                    ))}
                </div>
            ) : listings.length === 0 ? (
                <Card className="border-dashed p-10 text-center" data-testid="market-empty">
                    <Gavel className="h-10 w-10 mx-auto text-brand mb-3" />
                    <h3 className="font-heading text-lg font-semibold">No auctions match those filters</h3>
                    <p className="text-muted-foreground text-sm mt-1 mb-5">
                        Loosen a filter, or check back — new slots go live as creators list them.
                    </p>
                    {hasFilters ? (
                        <Button variant="outline" onClick={clearAll} className="font-semibold">Clear filters</Button>
                    ) : null}
                </Card>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="market-grid">
                    {listings.map((listing) => (
                        <MarketCard key={listing.id} listing={listing} />
                    ))}
                </div>
            )}
        </div>
    );
}

